import * as THREE from 'three'
import React, { useMemo, useState, useEffect } from 'react'
import { useLoader, useThree } from '@react-three/fiber';

import { delModel, extrudeSettings, roofPattern, textureAnisotropy } from '../../../../../../utils/Functions';
import { MetalStripeImg, NormalStripeImg } from '../../../../../../utils/Textures';
import { const_var, params } from '../../../../../../redux/reducers/reducer';

import { CSG } from '../../../../../../assets/libs/csg-lib';
import useMouseEvents from '../../../../../../hooks/useMouseEvents';
import { controls } from '../../../../../../redux/reducers/eventReducer';

const WallModel = () => {
    const { gl } = useThree ();
    const texture = const_var.metalTexture;
    // const verticalTexture = const_var.wallTextureObj[params.wallTexture];

    const [frontWallCloseState, setFrontWallCloseState] = useState(0);
    const [backWallCloseState, setBackWallCloseState] = useState(0);
    const [sideWallCloseState, setSideWallCloseState] = useState(0);
    const [sideWallLeftPorchSize, setSideWallLeftPorchSize] = useState(0);
    const [sideWallRightPorchSize, setSideWallRightPorchSize] = useState(0);
    const [isStorageRightWall, setIsStorageRightWall] = useState(false);

    const wallThickness = 0.005;
    const cbFrontBackWallOffset = 0.16;
    const offsetWithDoorThickness = 0.127;

    const canvasElement = document.getElementsByClassName("building-size")
    const isPattern = (canvasElement != undefined && canvasElement[0] && canvasElement[0].clientWidth > 1050 && !params.p_v_w) ? true : false

    let offset;
    
    const verticalTexture = useMemo(() => {
        let texture = const_var.wallTextureObj[params.wallTexture];
        textureAnisotropy(gl, texture,(params.wallTexture == "board_batten")? 0.7 : 1.3, (params.wallTexture == "lap")? 0.5 : 1.3, Math.PI/-2);
        return texture
    }, [params.p_v_w,params.wallTexture])

    const legDistance = useMemo(() => {
        return (const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5
    }, [const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost,const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s]])

    if(params.lean_p_d % legDistance === 0) offset = 0.15;
    else offset = 0;

    // const wainscotColor = useMemo(() => {
    //     if ((controls.isCheckActiveTab === 'doorwindow' && const_var.focusedSection !== 'leftlean' && const_var.focusedSection !== '') || (params.is_translusant)) {
    //         return 'white'
    //     } else {
    //         return params.p_w_c_c.replace("0x","#");
    //     }
    // }, [params.p_w_c_c, controls.isCheckActiveTab, const_var.focusedSection, params.is_translusant])
    const wainscotColor = useMemo(() => (params.p_w_c_c.replace("0x","#")), [params.p_w_c_c]) 
    
    let patternLength = params.p_v_w == true ? params.lean_p_w : params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + ((params.p_w_c_n && params.p_b_c_b_l_f === 'Close') ? -2.935 : 0)
    let patternDepth = params.p_v_w == true ? params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w))  + ((params.p_w_c_n && params.p_b_c_b_l_f === 'Close') ? -2.935 : 0) : params.lean_p_w
    let verticalHorizontalPos = params.p_v_w == true ? {"x":params.p_w / -2 ,"y": ((params.p_w_c_n && params.p_b_c_b_l_f === 'Close') ? 2.935 : 0), "z":params.lean_p_d / 2 + 0.16} : {"x": params.p_w / -2  - params.lean_p_w,"y" : (params.p_w_c_n && params.p_b_c_b_l_f === 'Close' ? 2.935 : 0), "z" : params.lean_p_d / 2 + 0.16}
    let verticalHorizontalRot = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : 0} : {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
    let lengthPattern = (params.p_v_w) ? patternLength : 3
    let depthPattern = (params.p_v_w) ? 3 : patternDepth

    const dutchDelModelFront = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_f === "Half Dutch End" || params.p_b_c_b_l_f === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_f === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_f])

    const dutchDelModelBack = useMemo(() => {
        const freeSpace = 1;
        let cornerFreeSpaceW = (params.lean_p_w - freeSpace) / 6;
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.lean_p_h - ((params.b_l_t_r_p / 12) * params.lean_p_w) :  params.lean_p_h
        let cornerFreeSpaceH = (leftHeight - freeSpace) / 6
        if (cornerFreeSpaceH > freeSpace) cornerFreeSpaceH = freeSpace * 2;

        if (cornerFreeSpaceW > freeSpace) cornerFreeSpaceW = freeSpace * 2;
        
        if (params.add_left_lean && (params.p_b_c_b_l_b === "Half Dutch End" || params.p_b_c_b_l_b === "Half End")) {
            const model = new THREE.Shape();
            model.moveTo(-(params.lean_p_w / 2 - freeSpace), 0);

            if (params.p_b_c_b_l_b === "Half Dutch End") {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
                model.lineTo(-(params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace - cornerFreeSpaceW), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace - cornerFreeSpaceH);
            } else {
                model.lineTo(-(params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
                model.lineTo((params.lean_p_w / 2 - freeSpace), leftHeight - freeSpace);
            }
            model.lineTo((params.lean_p_w / 2 - freeSpace), 0);

            return model;
        } else {
            return []
        }
    }, [params.add_left_lean, params.lean_p_w, params.lean_p_h, params.p_b_c_b_l_b])
    
    // left Lean front wall - CSG
    const frontWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_f == 'Open') return;
        let outerModel;
        let innerModel;
        let wainscotModel;

        const frontWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_f !== 'Open' || typeof(params.p_b_c_b_l_f) === 'object') {
            if (params.p_r_s === 1) {
                frontWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h - 0.25);
                frontWallModel.lineTo(-params.lean_p_w + 0.015, params.lean_p_h - 0.15);
                frontWallModel.lineTo(-params.lean_p_w + 0.05, params.lean_p_h - 0.05);
                frontWallModel.lineTo(-params.lean_p_w + 0.13, params.lean_p_h + 0.13 * params.b_l_t_r_p / 12);
                frontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                frontWallModel.lineTo(0, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.lineTo(-params.lean_p_w + 0.005, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.closePath();
            } else {
                frontWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h);
                frontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                frontWallModel.lineTo(0, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.lineTo(-params.lean_p_w + 0.005, frontWallCloseState + (params.p_r_s === 1 && 0.1));
                frontWallModel.closePath();
            }
        };

        let wallpanelValue = (params.p_r_s === 1) ?  frontWallCloseState + 0.1 : frontWallCloseState
        const frontWallCutModel = new THREE.Shape();

        frontWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w))); //right lean left top
        frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h); //right lean right top
        frontWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), wallpanelValue); //right lean right bottom
        frontWallCutModel.lineTo(-(params.p_w / 2), wallpanelValue); //right lean left bottom
        frontWallCutModel.closePath();

        const meshFrontWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(frontWallModel, extrudeSettings(0.005)));
        const meshFrontDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanFront', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelFront, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshFrontWallModel_Temp = meshFrontWallModel.clone();
        meshFrontWallModel_Temp.position.set(-params.p_w / 2, 0, params.lean_p_d / 2 + cbFrontBackWallOffset);
        meshFrontWallModel_Temp.updateMatrixWorld();
    
        let meshFrontDelModel_Temp = meshFrontDelModel.clone();
        meshFrontDelModel_Temp.position.set(0, 0, params.lean_p_d / 2 - 0.5);
        meshFrontDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.rotateY(Math.PI / 2);
        meshSideDelModel_Temp.updateMatrixWorld();

        let meshDutchDelModel_Temp = meshDutchDelModel.clone();
        meshDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w / 2),0, params.lean_p_d / 2 - 0.5);
        meshDutchDelModel_Temp.updateMatrixWorld();
    
        const meshFrontWallModel_CSG = CSG.fromMesh(meshFrontWallModel_Temp);
        const meshFrontDelModel_CSG = CSG.fromMesh(meshFrontDelModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
        const meshDutchDelModel_CSG = CSG.fromMesh(meshDutchDelModel_Temp);
    
        const frontWallModel_CSG = meshFrontWallModel_CSG.subtract(meshFrontDelModel_CSG);
        const meshSideInnerDelModel_CSG =  frontWallModel_CSG.subtract(meshSideDelModel_CSG);
        const meshFrontInnerModel_CSG = meshSideInnerDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGFrontWallModel = CSG.toMesh(
            meshFrontInnerModel_CSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGFrontWallModel.geometry;

        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength,isPattern), extrudeSettings(patternDepth)))  
        const meshGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(frontWallCutModel, extrudeSettings(1)))
    
        let meshGableDelModel_Temp = meshGableDelModel.clone();
        meshGableDelModel_Temp.position.set(0,0,params.lean_p_d / 2 - 0.5);
        meshGableDelModel_Temp.updateMatrixWorld();
    
        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshWallModel_Temp.updateMatrixWorld();

        let meshSideOuterDelModel_Temp = meshSideDelModel.clone();
        meshSideOuterDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideOuterDelModel_Temp.rotateY(Math.PI / 2);
        meshSideOuterDelModel_Temp.updateMatrixWorld();
    
        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshGableDelModel_CSG = CSG.fromMesh(meshGableDelModel_Temp);
        const meshSideOuterDelModel_CSG = CSG.fromMesh(meshSideOuterDelModel_Temp);
    
        const wallModel_CSG = meshWallModel_CSG.subtract(meshFrontDelModel_CSG);
        const GableModel_CSG = wallModel_CSG.intersect(meshGableDelModel_CSG);
        const SideOuterDelModel_CSG = GableModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const MeshFrontOuterModel_CSG = SideOuterDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGWallModel = CSG.toMesh(MeshFrontOuterModel_CSG,new THREE.Matrix4());

        outerModel = CSGWallModel.geometry;

        /************** cutting hole in wainscot using CSG for Front***************/

        let wainscotVerticalHorizontalPos = params.p_v_w == true ? {"x":params.p_w / -2 ,"y": 0, "z":params.lean_p_d / 2 + 0.16} : {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : params.lean_p_d / 2 + 0.16}
        let wainscotVerticalHorizontalRot = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : 0} : {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        
        const meshFrontWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern,isPattern), extrudeSettings(depthPattern)))
        let meshFrontWainscotModel_Temp = meshFrontWainscotModel.clone();
        meshFrontWainscotModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshFrontWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshFrontWainscotModel_Temp.updateMatrixWorld();

        const meshFrontWainscotModel_CSG = CSG.fromMesh(meshFrontWainscotModel_Temp);

        const frontWainscotModel_CSG = meshFrontWainscotModel_CSG.subtract(meshFrontDelModel_CSG);
        const sideDelWainscotModel_CSG = frontWainscotModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const finalFrontwainscotModel_CSG = sideDelWainscotModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGFrontWainscotModel = CSG.toMesh(
            finalFrontwainscotModel_CSG,
            new THREE.Matrix4()
            )

        wainscotModel = CSGFrontWainscotModel.geometry;

        return {outerModel, innerModel, wainscotModel};

    }, [params.wallTexture,params.p_h, params.p_w, params.p_r_p, verticalHorizontalPos, verticalHorizontalRot, lengthPattern, params.lean_p_pos, depthPattern, patternLength, patternDepth, params.p_w, params.lean_p_d, params.p_b_c_b_l_f, params.p_r_s, params.lean_p_w, params.lean_p_h, params.b_l_t_r_p, frontWallCloseState, const_var.newDoorsArray, params.p_w_c_n]);    

    // left Lean back wall - CSG
    const backWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l_b == 'Open' && !params.add_storage_check) return;
        let outerModel;
        let innerModel;
        let wainscotModel;

        const backWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_b !== 'Open' || typeof(params.p_b_c_b_l_b) === 'object') {
            if (params.p_r_s === 1) {
                backWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h - 0.25);
                backWallModel.lineTo(-params.lean_p_w + 0.015, params.lean_p_h - 0.15);
                backWallModel.lineTo(-params.lean_p_w + 0.05, params.lean_p_h - 0.05);
                backWallModel.lineTo(-params.lean_p_w + 0.13, params.lean_p_h + 0.13 * params.b_l_t_r_p / 12);
                backWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                backWallModel.lineTo(0, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.lineTo(-params.lean_p_w + 0.005, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.closePath();
            } else {
                backWallModel.moveTo(-params.lean_p_w + 0.005, params.lean_p_h);
                backWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                backWallModel.lineTo(0, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.lineTo(-params.lean_p_w + 0.005, backWallCloseState + (params.p_r_s === 1 && 0.1));
                backWallModel.closePath();
            }
        }

        let wallpanelValue = (params.p_r_s === 1) ?  backWallCloseState + 0.1 : backWallCloseState
        const backWallCutModel = new THREE.Shape();
        backWallCutModel.moveTo(-(params.p_w / 2), params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w))); //right lean left top
        backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), params.lean_p_h); //right lean right top
        backWallCutModel.lineTo(-(params.p_w / 2 + params.lean_p_w), wallpanelValue); //right lean right bottom
        backWallCutModel.lineTo(-(params.p_w / 2), wallpanelValue); //right lean left bottom
        backWallCutModel.closePath()
        
        const meshBackWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(backWallModel, extrudeSettings(0.005)));
        const meshBackDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanBack', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshDutchDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(dutchDelModelBack, extrudeSettings(1)), new THREE.MeshStandardMaterial({color: 'blue'}))

        let meshBackWallModel_Temp = meshBackWallModel.clone();
        meshBackWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 - cbFrontBackWallOffset);
        meshBackWallModel_Temp.updateMatrixWorld();
    
        let meshBackDelModel_Temp = meshBackDelModel.clone();
        meshBackDelModel_Temp.position.set(0, 0, -params.lean_p_d / 2 - 0.5);
        meshBackDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.rotateY(Math.PI / 2);
        meshSideDelModel_Temp.updateMatrixWorld();

        let meshDutchDelModel_Temp = meshDutchDelModel.clone();
        meshDutchDelModel_Temp.position.set(-(params.p_w / 2 + params.lean_p_w / 2),0, - params.lean_p_d / 2 - 0.5);
        meshDutchDelModel_Temp.updateMatrixWorld();

        const meshBackWallModel_CSG = CSG.fromMesh(meshBackWallModel_Temp);
        const meshBackDelModel_CSG = CSG.fromMesh(meshBackDelModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
        const meshDutchDelModel_CSG = CSG.fromMesh(meshDutchDelModel_Temp);

        const backWallModel_CSG = meshBackWallModel_CSG.subtract(meshBackDelModel_CSG);
        const meshSideInnerDelModel_CSG =  backWallModel_CSG.subtract(meshSideDelModel_CSG);
        const meshBackInnerModel_CSG = meshSideInnerDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGBackWallModel = CSG.toMesh(
            meshBackInnerModel_CSG,
            new THREE.Matrix4()
        )
    
        innerModel = CSGBackWallModel.geometry;

        let verticalHorizontalPosB = params.p_v_w == true ? {"x":params.p_w / -2 - params.lean_p_w,"y": ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? 2.935 : 0), "z":params.lean_p_d / -2 - 0.16} : {"x": params.p_w / -2,"y" : ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? 2.935 : 0), "z" : params.lean_p_d / -2 - 0.16}
        let verticalHorizontalRotB = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : Math.PI} : {"x":0,"y" : Math.PI / -2,"z" : Math.PI / 2}
        let patternLengthB = params.p_v_w == true ? params.lean_p_w : params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? -2.935 : 0)
        let patternDepthB = params.p_v_w == true ? params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w))  + ((params.p_w_c_n && params.p_b_c_b_l_b === 'Close') ? -2.935 : 0) : params.lean_p_w

        const meshWallModelB = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthB,isPattern), extrudeSettings(patternDepthB)))  
        const meshGableDelModelB = new THREE.Mesh(new THREE.ExtrudeGeometry(backWallCutModel, extrudeSettings(1)))
    
        let meshGableDelModel_TempB = meshGableDelModelB.clone();
        meshGableDelModel_TempB.position.set(0,0,params.lean_p_d / -2 - 0.5);
        meshGableDelModel_TempB.updateMatrixWorld();
    
        let meshWallModel_TempB = meshWallModelB.clone();
        meshWallModel_TempB.position.set(verticalHorizontalPosB.x,verticalHorizontalPosB.y,verticalHorizontalPosB.z);
        meshWallModel_TempB.rotation.set(verticalHorizontalRotB.x,verticalHorizontalRotB.y,verticalHorizontalRotB.z);
        meshWallModel_TempB.updateMatrixWorld();

        let meshSideOuterDelModel_Temp = meshSideDelModel.clone();
        meshSideOuterDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.8, 0, -params.lean_p_pos);
        meshSideOuterDelModel_Temp.rotateY(Math.PI / 2);
        meshSideOuterDelModel_Temp.updateMatrixWorld();
    
        const meshWallModel_CSGB = CSG.fromMesh(meshWallModel_TempB);
        const meshGableDelModel_CSGB = CSG.fromMesh(meshGableDelModel_TempB);
        const meshSideOuterDelModel_CSG = CSG.fromMesh(meshSideOuterDelModel_Temp);
        
    
        const wallModel_CSGB = meshWallModel_CSGB.subtract(meshBackDelModel_CSG);
        const GableModel_CSGB = wallModel_CSGB.intersect(meshGableDelModel_CSGB);
        const SideOuterDelModel_CSG = GableModel_CSGB.subtract(meshSideOuterDelModel_CSG);
        const MeshFrontOuterModel_CSG = SideOuterDelModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGWallModelB = CSG.toMesh(MeshFrontOuterModel_CSG,new THREE.Matrix4());

        outerModel = CSGWallModelB.geometry;

            /************** cutting hole in wainscot using CSG for Back***************/
        let wainscotVerticalHorizontalPosB = params.p_v_w == true ? {"x":params.p_w / -2 - params.lean_p_w,"y": 0, "z":params.lean_p_d / -2 - 0.16} : {"x": params.p_w / -2,"y" : 0, "z" : params.lean_p_d / -2 - 0.16}
        let wainscotVerticalHorizontalRotB = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : Math.PI} : {"x":0,"y" : Math.PI / -2,"z" : Math.PI / 2}
        let lengthPatternB = (params.p_v_w) ? patternLengthB : 3
        let depthPatternB = (params.p_v_w) ? 3 : patternDepthB

        const meshBackWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternB,isPattern), extrudeSettings(depthPatternB)))
        let meshBackWainscotModel_Temp = meshBackWainscotModel.clone();
        meshBackWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosB.x,wainscotVerticalHorizontalPosB.y,wainscotVerticalHorizontalPosB.z);
        meshBackWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotB.x,wainscotVerticalHorizontalRotB.y,wainscotVerticalHorizontalRotB.z);
        meshBackWainscotModel_Temp.updateMatrixWorld();

        const meshBackWainscotModel_CSG = CSG.fromMesh(meshBackWainscotModel_Temp);

        const backWainscotModel_CSG = meshBackWainscotModel_CSG.subtract(meshBackDelModel_CSG);
        const sideDelWainscotModel_CSG = backWainscotModel_CSG.subtract(meshSideOuterDelModel_CSG);
        const finalFrontwainscotModel_CSG = sideDelWainscotModel_CSG.subtract(meshDutchDelModel_CSG);

        const CSGBackWainscotModel = CSG.toMesh(
            finalFrontwainscotModel_CSG,
            new THREE.Matrix4()
            )

            wainscotModel = CSGBackWainscotModel.geometry;

        return {innerModel, outerModel, wainscotModel}
    }, [params.wallTexture,frontWallCloseState, params.p_v_w, params.p_h, params.p_w, params.p_r_p, params.lean_p_pos, cbFrontBackWallOffset, params.p_b_c_b_l_b, params.p_r_s, params.lean_p_w, params.lean_p_d, params.b_l_t_r_p, params.lean_p_h, backWallCloseState, const_var.newDoorsArray, params.p_w_c_n]);

    // left Lean side wall - CSG
    const sideWallModelObj = useMemo(() => {
        if(params.p_b_c_b_l == 'Open' && !params.add_storage_check) return;
        let innerModel;
        let outerModel;
        let wainscotModel;

        const sideWallModel = new THREE.Shape();

        sideWallModel.moveTo((params.add_storage_check ? params.lean_p_d / 2 - parseInt(params.add_storage) - 0.02 : -(params.lean_p_d / 2 + cbFrontBackWallOffset)), (params.add_storage_check ? 0 : sideWallCloseState));
        sideWallModel.lineTo((params.add_storage_check ? params.lean_p_d / 2 - parseInt(params.add_storage) - 0.02 : -(params.lean_p_d / 2 + cbFrontBackWallOffset)), sideWallCloseState);
        sideWallModel.lineTo(-(params.lean_p_d / 2 + sideWallLeftPorchSize + cbFrontBackWallOffset), sideWallCloseState);
        sideWallModel.lineTo(-(params.lean_p_d / 2 + sideWallLeftPorchSize + cbFrontBackWallOffset), params.lean_p_h + (params.p_r_s === 1 && -0.25));
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset) + sideWallRightPorchSize, params.lean_p_h + (params.p_r_s === 1 && -0.25));
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset) + sideWallRightPorchSize, sideWallCloseState);
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset), sideWallCloseState);
        sideWallModel.lineTo((params.lean_p_d / 2 + cbFrontBackWallOffset), (params.add_storage_check ? 0 : sideWallCloseState));
        sideWallModel.closePath();
        
        let lengthR = params.lean_p_d / 2
        let lengthL = params.lean_p_d / 2

        if(params.add_left_front_lean_porch && !params.add_left_back_lean_porch){
            lengthR = params.lean_p_d / 2 + params.lean_p_w
        } else if(!params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            lengthL = params.lean_p_d / 2 + params.lean_p_w
        } else if(params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            lengthL = params.lean_p_d / 2 + params.lean_p_w
            lengthR = params.lean_p_d / 2 + params.lean_p_w
        }

        let wallpanelValue = (sideWallCloseState < 0) ? params.lean_p_h + (params.p_r_s === 1 && -0.25) : sideWallCloseState
        const sideWallCutModel = new THREE.Shape();
        sideWallCutModel.moveTo(lengthR, params.lean_p_h + (params.p_r_s === 1 && -0.25));
        sideWallCutModel.lineTo(-(lengthL), params.lean_p_h + (params.p_r_s === 1 && -0.25));
        sideWallCutModel.lineTo(-(lengthL), wallpanelValue);
        if(params.add_storage_check){
            sideWallCutModel.lineTo(-(params.lean_p_d / 2), wallpanelValue);
            sideWallCutModel.lineTo(-(params.lean_p_d / 2), 0);
            sideWallCutModel.lineTo(-params.lean_p_d / 2 + Number(params.add_storage) + 0.02, 0);
            sideWallCutModel.lineTo(-params.lean_p_d / 2 + Number(params.add_storage) + 0.02, wallpanelValue);
        }
        sideWallCutModel.lineTo(lengthR, wallpanelValue);
        
        const meshSideWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallModel, extrudeSettings(0.005)));
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshSideWallModel_Temp = meshSideWallModel.clone();
        meshSideWallModel_Temp.rotateY(Math.PI / 2);
        meshSideWallModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w, 0, 0);
        meshSideWallModel_Temp.updateMatrixWorld();
    
        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.rotateY(Math.PI / 2)
        // meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w -0.5, 0, alignPosOffset);
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w -0.5, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.updateMatrixWorld();
    
        const meshSideWallModel_CSG = CSG.fromMesh(meshSideWallModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
    
        const sideWallModel_CSG = meshSideWallModel_CSG.subtract(meshSideDelModel_CSG);
        const CSGSideWallModel = CSG.toMesh(
            sideWallModel_CSG,
            new THREE.Matrix4()
        )

        innerModel = CSGSideWallModel.geometry;
    
        let verticalDepth = params.lean_p_d
        let horizontalDepth = params.lean_p_d
        let VSPosition = params.lean_p_d / 2
        let HSPosition = params.lean_p_d / -2
        if(params.add_left_front_lean_porch && !params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2 + params.lean_p_w
            verticalDepth = params.lean_p_d + params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w
        } else if(!params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2
            HSPosition = params.lean_p_d / -2 -  params.lean_p_w
            verticalDepth = params.lean_p_d +  params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w
        } else if(params.add_left_front_lean_porch && params.add_left_back_lean_porch){
            VSPosition = params.lean_p_d / 2 + params.lean_p_w
            HSPosition = params.lean_p_d / -2 - params.lean_p_w
            verticalDepth = params.lean_p_d +  params.lean_p_w + params.lean_p_w
            horizontalDepth = params.lean_p_d +  params.lean_p_w + params.lean_p_w
        }
    
        let verticalHorizontalPosS = params.p_v_w == true ? {"x":params.p_w / -2 - params.lean_p_w,"y": ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? 2.935 : 0), "z":VSPosition} : {"x": params.p_w / -2  - params.lean_p_w,"y" : ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? 2.935 : 0), "z" : HSPosition}
        let verticalHorizontalRotS = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : Math.PI / -2} : {"x":0,"y" : 0,"z" : Math.PI / 2}
        let patternLengthS = params.p_v_w == true ? verticalDepth : params.lean_p_h + ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? -2.935 : 0)
        let patternDepthS = params.p_v_w == true ? params.lean_p_h + ((params.p_w_c_n && (params.p_b_c_b_l === 'Close' || params.add_storage_check)) ? -2.935 : 0) : horizontalDepth
        const meshSideWallModelPattern = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthS,isPattern), extrudeSettings(patternDepthS)));
        const meshWallDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(sideWallCutModel, extrudeSettings(1)))
        
        let meshWallModel_TempS = meshSideWallModelPattern.clone();
        meshWallModel_TempS.position.set(verticalHorizontalPosS.x,verticalHorizontalPosS.y,verticalHorizontalPosS.z);
        meshWallModel_TempS.rotation.set(verticalHorizontalRotS.x,verticalHorizontalRotS.y,verticalHorizontalRotS.z);
        meshWallModel_TempS.updateMatrixWorld();
    
    
        let meshWallDelModel_TempS = meshWallDelModel.clone();
        meshWallDelModel_TempS.position.set(-params.p_w / 2 - params.lean_p_w + 0.5 , 0, 0);
        meshWallDelModel_TempS.rotation.set(0,Math.PI / -2,0)
        meshWallDelModel_TempS.updateMatrixWorld();
    
        const meshWallModel_CSGS = CSG.fromMesh(meshWallModel_TempS);
        const meshWallDelModel_CSGS = CSG.fromMesh(meshWallDelModel_TempS);
    
    
        const wallModel_CSGS = meshWallModel_CSGS.subtract(meshSideDelModel_CSG);
        const meshWallDel_CSGS = wallModel_CSGS.intersect(meshWallDelModel_CSGS);
        const CSGWallModelS = CSG.toMesh(meshWallDel_CSGS,new THREE.Matrix4());

        outerModel = CSGWallModelS.geometry;

            /************** cutting hole in wainscot using CSG for Side***************/
        let wainscotVerticalHorizontalPosS = params.p_v_w == true ? {"x":params.p_w / -2 - params.lean_p_w,"y": 0, "z":VSPosition} : {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : HSPosition}
        let wainscotVerticalHorizontalRotS = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : Math.PI / -2} : {"x":0,"y" : 0,"z" : Math.PI / 2}
        let lengthPatternS = (params.p_v_w) ? patternLengthS : 3
        let depthPatternS = (params.p_v_w) ? 3 : patternDepthS

        const meshSideWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternS,isPattern), extrudeSettings(depthPatternS)))
        let meshSideWainscotModel_Temp = meshSideWainscotModel.clone();
        meshSideWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosS.x,wainscotVerticalHorizontalPosS.y,wainscotVerticalHorizontalPosS.z);
        meshSideWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotS.x,wainscotVerticalHorizontalRotS.y,wainscotVerticalHorizontalRotS.z);
        meshSideWainscotModel_Temp.updateMatrixWorld();

        const meshSideWainscotModel_CSG = CSG.fromMesh(meshSideWainscotModel_Temp);

        const SideWainscotModel_CSG = meshSideWainscotModel_CSG.subtract(meshSideDelModel_CSG);
        const SideWainscotModelCSG = SideWainscotModel_CSG.intersect(meshWallDelModel_CSGS);
        const CSGSideWainscotModel = CSG.toMesh(
            SideWainscotModelCSG,
            new THREE.Matrix4()
            )

        wainscotModel = CSGSideWainscotModel.geometry;

        return {innerModel, outerModel, wainscotModel}

    }, [params.wallTexture,params.lean_p_pos, params.p_v_w, params.p_w, params.lean_p_w, params.p_r_s, params.lean_p_d, sideWallLeftPorchSize, params.lean_p_h, sideWallRightPorchSize, sideWallCloseState, params.add_storage_check, params.add_storage, params.add_left_front_lean_porch, params.add_left_back_lean_porch, const_var.newDoorsArray,params.p_b_c_b_l, params.p_w_c_n]);

    // left Lean Front storage Wall - CSG
    const storageFrontWallModelObj = useMemo(() => {
        if(!params.add_storage_check) return;

        let mainWall;
        let innerWall;
        let wainscotWall;

        const storageFrontWallModel = new THREE.Shape();

        if (params.p_b_c_b_l_b !== 'Open' || typeof(params.p_b_c_b_l_b) === 'object') {
            if (params.p_r_s === 1) {
                storageFrontWallModel.moveTo(-params.lean_p_w + 0.005 + offsetWithDoorThickness - 0.12, params.lean_p_h - 0.25);
                storageFrontWallModel.lineTo(-params.lean_p_w + 0.015 + offsetWithDoorThickness - 0.12, params.lean_p_h - 0.15);
                storageFrontWallModel.lineTo(-params.lean_p_w + 0.05 + offsetWithDoorThickness - 0.12, params.lean_p_h - 0.05);
                storageFrontWallModel.lineTo(-params.lean_p_w + 0.13 + offsetWithDoorThickness - 0.12, params.lean_p_h + 0.13 * params.b_l_t_r_p / 12);
                storageFrontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                storageFrontWallModel.lineTo(0, backWallCloseState);
                storageFrontWallModel.lineTo(-params.lean_p_w + 0.005 + offsetWithDoorThickness - 0.12, backWallCloseState);
                storageFrontWallModel.closePath();
            } else {
                storageFrontWallModel.moveTo(-params.lean_p_w + offsetWithDoorThickness - 0.12, params.lean_p_h);
                storageFrontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p);
                storageFrontWallModel.lineTo(0, backWallCloseState);
                storageFrontWallModel.lineTo(-params.lean_p_w + offsetWithDoorThickness - 0.12, backWallCloseState);
                storageFrontWallModel.closePath();
            }
        }
                
        let verticalHorizontalPosStorageF = params.p_v_w == true ? {"x":params.p_w / -2,"y": (params.p_w_c_n ? 2.935 : 0), "z":params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness} : {"x": params.p_w / -2  - params.lean_p_w,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness}
        let verticalHorizontalRotStorageF = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : 0} : {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let patternLengthStorageF = params.p_v_w == true ? params.lean_p_w : params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + (params.p_w_c_n ? -2.935 : 0)
        let patternDepthStorageF = params.p_v_w == true ? params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w))  + (params.p_w_c_n ? -2.935 : 0) : params.lean_p_w
    
        const meshStorageWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthStorageF,isPattern), extrudeSettings(patternDepthStorageF))) 
        const meshFrontStorageWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageFrontWallModel, extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageFrontWallModel, extrudeSettings(wallThickness)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanStorageFront', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshStorageWallModel_Temp = meshStorageWallModel.clone();
        meshStorageWallModel_Temp.position.set(verticalHorizontalPosStorageF.x,verticalHorizontalPosStorageF.y,verticalHorizontalPosStorageF.z);
        meshStorageWallModel_Temp.rotation.set(verticalHorizontalRotStorageF.x,verticalHorizontalRotStorageF.y,verticalHorizontalRotStorageF.z);
        meshStorageWallModel_Temp.updateMatrixWorld();
    
        let meshFrontStorageWallModel_Temp = meshFrontStorageWallModel.clone();
        meshFrontStorageWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 + parseInt(params.add_storage) - 0.5)
        meshFrontStorageWallModel_Temp.updateMatrixWorld();
        
        let meshFrontStorageInnerWallModel_Temp = meshFrontStorageInnerWallModel.clone();
        meshFrontStorageInnerWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 + parseInt(params.add_storage))
        meshFrontStorageInnerWallModel_Temp.updateMatrixWorld();

        let meshFrontStorageDelModel_Temp = meshFrontStorageDelModel.clone();
        meshFrontStorageDelModel_Temp.position.set(0, 0, parseInt(params.add_storage) - 0.5 -params.lean_p_d / 2);
        meshFrontStorageDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.75, 0, -params.lean_p_pos);
        meshSideDelModel_Temp.rotateY(Math.PI / 2)
        meshSideDelModel_Temp.updateMatrixWorld();
    
        const meshFrontStorageWallModel_CSG = CSG.fromMesh(meshFrontStorageWallModel_Temp);
        const meshFrontStorageInnerWallModel_CSG = CSG.fromMesh(meshFrontStorageInnerWallModel_Temp);
        const meshFrontStorageDelModel_CSG = CSG.fromMesh(meshFrontStorageDelModel_Temp);
        const meshStorageWallModel_CSG = CSG.fromMesh(meshStorageWallModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
    
        const frontStorageWallModel_CSG =  meshFrontStorageWallModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const frontStorageInnerWallModel_CSG =  meshFrontStorageInnerWallModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const storageSideInnerWallDelModel_CSG = frontStorageInnerWallModel_CSG.subtract(meshSideDelModel_CSG);

        const storageWallModel_CSG = meshStorageWallModel_CSG.intersect(frontStorageWallModel_CSG);
        const storageSideWallDelModel_CSG = storageWallModel_CSG.subtract(meshSideDelModel_CSG);

        const CSGFrontStorageWallModel = CSG.toMesh(storageSideWallDelModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))
        const CSGFrontStorageInnerWallModel = CSG.toMesh(storageSideInnerWallDelModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))

        mainWall = CSGFrontStorageWallModel.geometry;
        innerWall = CSGFrontStorageInnerWallModel.geometry;

            /************** cutting hole in wainscot using CSG for Storage Front***************/
        let wainscotVerticalHorizontalPosStorageF = params.p_v_w == true ? {"x":params.p_w / -2,"y": 0, "z":params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness} : {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness}
        let wainscotVerticalHorizontalRotStorageF = params.p_v_w == true ? {"x":Math.PI / 2,"y" : Math.PI,"z" : 0} : {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let lengthPatternStorageF = (params.p_v_w) ? patternLengthStorageF : 3
        let depthPatternStorageF = (params.p_v_w) ? 3 : patternDepthStorageF

        const meshStorageFrontWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternStorageF,isPattern), extrudeSettings(depthPatternStorageF))) 
        let meshStorageFrontWainscotModel_Temp = meshStorageFrontWainscotModel.clone();
        meshStorageFrontWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosStorageF.x,wainscotVerticalHorizontalPosStorageF.y,wainscotVerticalHorizontalPosStorageF.z);
        meshStorageFrontWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotStorageF.x,wainscotVerticalHorizontalRotStorageF.y,wainscotVerticalHorizontalRotStorageF.z);
        meshStorageFrontWainscotModel_Temp.updateMatrixWorld();

        const meshStorageFrontWainscotModel_CSG = CSG.fromMesh(meshStorageFrontWainscotModel_Temp);

        const storageFrontWainscotModel_CSG = meshStorageFrontWainscotModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const storageSideDelModel_CSG = storageFrontWainscotModel_CSG.subtract(meshSideDelModel_CSG);

        const CSGStorageFrontWainscotModel = CSG.toMesh(
            storageSideDelModel_CSG,
            new THREE.Matrix4()
        )

        wainscotWall = CSGStorageFrontWainscotModel.geometry;

        return {mainWall, innerWall, wainscotWall}

    }, [params.wallTexture,params.b_l_t_r_p, params.add_storage, params.p_v_w, params.p_w, params.lean_p_d, params.p_b_c_b_l_b, params.lean_p_pos, params.p_r_s, params.lean_p_w, params.b_l_t_r_p, params.lean_p_h, backWallCloseState,const_var.newDoorsArray, params.add_storage_check]);
    
     // left Lean Front storage inWall - CSG
    const storageRightWallModelObj = useMemo(() => {
        if(!params.add_storage_check) return;

        let mainWall;
        let innerWall;
        let wainscotWall;
        
        const storageSideWallModel = new THREE.Shape();
        storageSideWallModel.moveTo(params.lean_p_d / 2 - parseInt(params.add_storage) - offset, 0);
        storageSideWallModel.lineTo(params.lean_p_d / 2 - parseInt(params.add_storage) - offset, params.lean_p_h + (params.lean_p_w  - 0.05)* params.b_l_t_r_p / 12);
        storageSideWallModel.lineTo(params.lean_p_d / 2 + cbFrontBackWallOffset - wallThickness, params.lean_p_h + (params.lean_p_w  - 0.05)* params.b_l_t_r_p / 12);
        storageSideWallModel.lineTo(params.lean_p_d / 2 + cbFrontBackWallOffset - wallThickness, 0);
        storageSideWallModel.closePath();

        let verticalHorizontalPosStorageS = params.p_v_w == true ? {"x":params.p_w / -2 - 0.06 ,"y": (params.p_w_c_n ? 2.935 : 0), "z":params.lean_p_d / -2} : {"x": params.p_w / -2 - 0.06,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.lean_p_d / 2 - 0.06}
        let verticalHorizontalRotStorageS = params.p_v_w == true ? {"x":Math.PI / -2,"y" : 0,"z" : Math.PI / -2} : {"x":0,"y" : Math.PI,"z" : Math.PI / 2}
        let patternLengthStorageS = params.p_v_w == true ? params.lean_p_d : params.lean_p_h + ((params.b_l_t_r_p/12) * params.lean_p_w) + (params.p_w_c_n ? -2.935 : 0)
        let patternDepthStorageS = params.p_v_w == true ? params.lean_p_h + ((params.b_l_t_r_p/12) * params.lean_p_w) + (params.p_w_c_n ? -2.935 : 0) : params.lean_p_d
   
        const meshSideStorageWallModelPattern = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthStorageS,isPattern), extrudeSettings(patternDepthStorageS)));
        const meshFrontStorageInWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageSideWallModel, extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageSideWallModel, extrudeSettings(wallThickness)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageInDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanStorageRight', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
    
        let mesStoragehWallModel_TempS = meshSideStorageWallModelPattern.clone();
        mesStoragehWallModel_TempS.position.set(verticalHorizontalPosStorageS.x,verticalHorizontalPosStorageS.y,verticalHorizontalPosStorageS.z);
        mesStoragehWallModel_TempS.rotation.set(verticalHorizontalRotStorageS.x,verticalHorizontalRotStorageS.y,verticalHorizontalRotStorageS.z);
        mesStoragehWallModel_TempS.updateMatrixWorld();
   
        let meshFrontStorageInWallModel_Temp = meshFrontStorageInWallModel.clone();
        meshFrontStorageInWallModel_Temp.rotateY(Math.PI / 2);
        meshFrontStorageInWallModel_Temp.position.set(-(params.p_w / 2 + 0.5), 0, 0);
        meshFrontStorageInWallModel_Temp.updateMatrixWorld();
        
        let meshFrontStorageInnerWallModel_Temp = meshFrontStorageInnerWallModel.clone();
        meshFrontStorageInnerWallModel_Temp.rotateY(Math.PI / 2);
        meshFrontStorageInnerWallModel_Temp.position.set(-(params.p_w / 2 + 0.07), 0, 0);
        meshFrontStorageInnerWallModel_Temp.updateMatrixWorld();

        let meshFrontStorageInDelModel_Temp = meshFrontStorageInDelModel.clone();
        meshFrontStorageInDelModel_Temp.rotateY(Math.PI / 2);
        meshFrontStorageInDelModel_Temp.position.set(-params.p_w / 2 - 0.5, 0, -params.lean_p_pos);
        meshFrontStorageInDelModel_Temp.updateMatrixWorld();
    
        const meshFrontStorageInWallModel_CSG = CSG.fromMesh(meshFrontStorageInWallModel_Temp);
        const meshFrontStorageInnerWallModel_CSG = CSG.fromMesh(meshFrontStorageInnerWallModel_Temp);
        const meshFrontStorageInDelModel_CSG = CSG.fromMesh(meshFrontStorageInDelModel_Temp);
        const meshStorageWallModel_CSGS = CSG.fromMesh(mesStoragehWallModel_TempS);
    
        const frontStorageInWallModel_CSG =  meshFrontStorageInWallModel_CSG.subtract(meshFrontStorageInDelModel_CSG);
        const frontStorageInnerWallModel_CSG =  meshFrontStorageInnerWallModel_CSG.subtract(meshFrontStorageInDelModel_CSG);
        const storageSideWallModel_CSG = meshStorageWallModel_CSGS.intersect(frontStorageInWallModel_CSG);
   
        const CSGFrontStorageInWallModel = CSG.toMesh(storageSideWallModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))
        const CSGFrontStorageInnerWallModel = CSG.toMesh(frontStorageInnerWallModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))

        mainWall = CSGFrontStorageInWallModel.geometry;
        innerWall = CSGFrontStorageInnerWallModel.geometry;

        /************** cutting hole in wainscot using CSG for Storage Right***************/
        let wainscotVerticalHorizontalPosStorageS = params.p_v_w == true ? {"x":params.p_w / -2 - 0.06 ,"y": 0, "z":params.lean_p_d / -2} : {"x": params.p_w / -2 - 0.06,"y" : 0, "z" : params.lean_p_d / 2 - 0.06}
        let wainscotVerticalHorizontalRotStorageS = params.p_v_w == true ? {"x":Math.PI / -2,"y" : 0,"z" : Math.PI / -2} : {"x":0,"y" : Math.PI,"z" : Math.PI / 2}
        let lengthPatternStorageS = (params.p_v_w) ? patternLengthStorageS : 3
        let depthPatternStorageS = (params.p_v_w) ? 3 : patternDepthStorageS

        const meshStorageRightWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternStorageS,isPattern), extrudeSettings(depthPatternStorageS)));
        let meshStorageRightWainscotModel_Temp = meshStorageRightWainscotModel.clone();
        meshStorageRightWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosStorageS.x,wainscotVerticalHorizontalPosStorageS.y,wainscotVerticalHorizontalPosStorageS.z);
        meshStorageRightWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotStorageS.x,wainscotVerticalHorizontalRotStorageS.y,wainscotVerticalHorizontalRotStorageS.z);
        meshStorageRightWainscotModel_Temp.updateMatrixWorld();

        const meshStorageRightWainscotModel_CSG = CSG.fromMesh(meshStorageRightWainscotModel_Temp);

        const storageRightWainscotModel_CSG = meshStorageRightWainscotModel_CSG.subtract(meshFrontStorageInDelModel_CSG);
        const frontStorageInWallModelCSG = storageRightWainscotModel_CSG.intersect(frontStorageInWallModel_CSG);

        const CSGStorageRightWainscotModel = CSG.toMesh(
            frontStorageInWallModelCSG,
            new THREE.Matrix4()
        )

        wainscotWall = CSGStorageRightWainscotModel.geometry;

        return {mainWall, innerWall, wainscotWall}

    }, [params.wallTexture,params.p_d,params.add_storage_check, params.lean_p_pos, params.p_v_w, params.p_w , params.b_l_t_r_p, params.lean_p_h, params.lean_p_d, params.lean_p_w, params.p_r_s, params.add_storage, const_var.newDoorsArray, params.p_w_c_n]);


    /*******************************  ****************************/

    useMemo(() => {
        textureAnisotropy(gl, texture, 1.3, 1.3, params.p_v_w ? Math.PI / 2 : 0);
    }, [params.p_v_w, gl, texture])

    // --- front wall set    
    useMemo(() => {
        let tempWallCloseState;
        if (params.p_b_c_b_l_f === "Gable") {
            if (params.p_r_s === 1) {
                tempWallCloseState = params.lean_p_h - 0.25;
            } else {
                tempWallCloseState = params.lean_p_h - 0.2;
            }
        } else if (params.p_b_c_b_l_f === "One_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l_f === "Half_Close") {
            tempWallCloseState = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l_f === "Three_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l_f === "Half Dutch End") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_f === "Half End") {
            tempWallCloseState = 0;
        } else if (params.p_b_c_b_l_f === "Close") {
            tempWallCloseState = 0;
        } else {
            tempWallCloseState = -1;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l_f == "Close") tempWallCloseState = 3;

        setFrontWallCloseState(tempWallCloseState);
    }, [params.p_b_c_b_l_f, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    // --- back wall set    
    useMemo(() => {
        let tempWallCloseState;
        if (params.add_storage_check) {
            tempWallCloseState = 0;
        } else {
            if (params.p_b_c_b_l_b === "Gable") {
                if (params.p_r_s === 1) {
                    tempWallCloseState = params.lean_p_h - 0.25;
                } else {
                    tempWallCloseState = params.lean_p_h - 0.2;
                }
            } else if (params.p_b_c_b_l_b === "One_Fourth_Close") {
                tempWallCloseState = params.lean_p_h / 4 * 3;
            } else if (params.p_b_c_b_l_b === "Half_Close") {
                tempWallCloseState = params.lean_p_h / 2;
            } else if (params.p_b_c_b_l_b === "Three_Fourth_Close") {
                tempWallCloseState = params.lean_p_h / 4;
            } else if (params.p_b_c_b_l_b === "Close") {
                tempWallCloseState = 0;
            } else if (params.p_b_c_b_l_b === "Half Dutch End") {
                tempWallCloseState = 0;
            } else if (params.p_b_c_b_l_b === "Half End") {
                tempWallCloseState = 0;
            } else {
                tempWallCloseState = -1;
            }
        }
        // if(params.p_w_c_n && params.p_b_c_b_l_b == "Close") tempWallCloseState = 3;
        setBackWallCloseState(tempWallCloseState);
    }, [params.add_storage_check, params.p_b_c_b_l_b, params.lean_p_h, params.p_r_s, params.p_w_c_n])

    // --- side wall set    
    useMemo(() => {
        let tempWallCloseState = 0;
        if (params.p_b_c_b_l === 'Open' || typeof(params.p_b_c_b_l) === 'object') {
            if (params.p_r_s === 1) {
                tempWallCloseState = params.lean_p_h - 0.25;
            } else {
                tempWallCloseState = params.lean_p_h;
            }
        } else if (params.p_b_c_b_l === "One_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4 * 3;
        } else if (params.p_b_c_b_l === "Half_Close") {
            tempWallCloseState = params.lean_p_h / 2;
        } else if (params.p_b_c_b_l === "Three_Fourth_Close") {
            tempWallCloseState = params.lean_p_h / 4;
        } else if (params.p_b_c_b_l === "Close") {
            tempWallCloseState = 0;
        } else {
            tempWallCloseState = params.lean_p_h - 3 * params.p_b_c_b_l;
        }

        // if(params.p_w_c_n && params.p_b_c_b_l == "Close") tempWallCloseState = 3;

        setSideWallCloseState(tempWallCloseState);
    }, [params.p_b_c_b_l, params.lean_p_h, params.p_r_s, params.p_w_c_n])
    
    // --- porch state
    useMemo(() => {
        if (params.add_left_front_lean_porch) {
            setSideWallLeftPorchSize(params.lean_p_w);
        } else {
            setSideWallLeftPorchSize(0);
        }
    }, [params.add_left_front_lean_porch, params.lean_p_w, params.p_r_s])

    useMemo(() => {
        if (params.add_left_back_lean_porch) {
            setSideWallRightPorchSize(params.lean_p_w);
        } else {
            setSideWallRightPorchSize(0);
        }
    }, [params.add_left_back_lean_porch, params.lean_p_w, params.p_r_s])

    const mainColor = useMemo(() => (params.p_w_c.replace("0x","#")), [params.p_w_c]) 
    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "leftlean" && params.activeWall === "L_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);

    useMemo(() => {
        let array = const_var.newDoorsArray.filter((item) => item.userData.wallName === 'c_b_l_w' && item.height > params.lean_p_h);
        for(let i=0; i<array.length; i++) {
            let item = array[i];
            const doorPositionX = item.positionX;
            const doorWidth = item.width
            const LeftStoragePos = params.lean_p_pos - params.lean_p_d / 2
            const RightStoragePos = params.lean_p_pos - params.lean_p_d / 2 + parseInt(params.add_storage)
            if((LeftStoragePos >= (doorPositionX - doorWidth / 2)) && (RightStoragePos <= (doorPositionX + doorWidth / 2))){
                setIsStorageRightWall(true)
                return;
            } 
            setIsStorageRightWall(false)
        }

    }, [const_var.newDoorsArray, params.lean_p_pos, params.lean_p_d, params.add_storage, params.lean_p_h])

    return (
        <>
            <group name='left_lean_side_wall' {...eventHandlers}>
                {(params.p_b_c_b_l !== 'Open' || params.add_storage_check) && sideWallModelObj?.outerModel &&
                    <mesh name="L_L_L_W" geometry={sideWallModelObj?.outerModel}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }
                {/* Side Wainscot */}
                {params.p_w_c_n == true && (params.p_b_c_b_l === "Close" || params.add_storage_check) && sideWallModelObj?.wainscotModel &&
                    <mesh name="L_L_L_W_wainscot" geometry={sideWallModelObj?.wainscotModel} position={[-0.005,0,0]} >
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={wainscotColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }
                {sideWallModelObj && sideWallModelObj?.innerModel && 
                    <mesh name="side" geometry={sideWallModelObj?.innerModel} position={[wallThickness + 0.01, 0, 0]}>
                            <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                    </mesh>
               }
            </group>
            <group name='out-side-wall'>
                {params.p_b_c_b_l_f !== 'Open' && frontWallModelObj?.outerModel &&
                    <mesh name="L_L_F_W" geometry={frontWallModelObj?.outerModel}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }
                {params.p_b_c_b_l_b !== 'Open' && backWallModelObj?.outerModel &&
                    <mesh name="L_L_B_W" geometry={backWallModelObj?.outerModel}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }

                {/* Front Wainscot */}
                {params.p_w_c_n == true && (params.p_b_c_b_l_f === "Close" || params.p_b_c_b_l_f === "Half Dutch End" || params.p_b_c_b_l_f === "Half End") && frontWallModelObj?.wainscotModel &&
                    <mesh name="L_L_F_W_wainscot" geometry={frontWallModelObj?.wainscotModel} position={[0,0,0.005]}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={wainscotColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }

                {/* Back Wainscot */} 
                {params.p_w_c_n == true && (params.p_b_c_b_l_b === "Close" || params.p_b_c_b_l_b === "Half Dutch End" || params.p_b_c_b_l_b === "Half End") && backWallModelObj?.wainscotModel &&
                    <mesh name="L_L_B_W_wainscot" geometry={backWallModelObj?.wainscotModel} position={[0,0,-0.005]}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={wainscotColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }

                {/* Storage Front Wainscot */}
                {params.p_w_c_n == true && params.add_storage_check && storageFrontWallModelObj?.wainscotWall &&
                    <mesh name="L_L_F_S_W_wainscot" geometry={storageFrontWallModelObj?.wainscotWall} position={[0,0,0.005 + offset]}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={wainscotColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }

                
                {/* Storage Right Front Wainscot */}
                {params.p_w_c_n == true && params.add_storage_check && storageRightWallModelObj?.wainscotWall &&
                    <mesh name="L_L_L_S_W_wainscot" geometry={storageRightWallModelObj?.wainscotWall}  position={[0.005,0,0]}>
                        <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={wainscotColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                    </mesh>
                }

                {params.add_storage_check && storageFrontWallModelObj?.mainWall && storageRightWallModelObj?.mainWall &&
                    <>
                        <mesh name="L_L_F_S_W" geometry={storageFrontWallModelObj?.mainWall} position={[0, 0, offset]}>
                            <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                        </mesh>
                        {!isStorageRightWall &&
                            <mesh name="L_L_R_S_W" geometry={storageRightWallModelObj?.mainWall} >
                                <meshStandardMaterial aoMap={verticalTexture} aoMapIntensity={(params.p_v_w || !isPattern) ? 0.6 : (params.wallTexture == "board_batten") ? 1 :  (params.wallTexture == "lap")? 0.6 : 0}  color={mainColor} side={THREE.DoubleSide} roughness={0.5} metalness={(params.p_v_w || params.wallTexture != "none") ? 0 : 0.2} toneMapped={false} emissive={'#101010'} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} needsUpdate={true}/>
                            </mesh>
                        }
                    </>
                }
            </group>
            <group name='in-side-wall'>
                {
                    frontWallModelObj && frontWallModelObj?.innerModel && <mesh name="lean_front_leg_wall" geometry={frontWallModelObj?.innerModel} position={[0, 0, -wallThickness * 3 - 0.01]}>
                        <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                    </mesh>
                }
                {
                    backWallModelObj && backWallModelObj?.innerModel && <mesh name="back" geometry={backWallModelObj?.innerModel} position={[0, 0, wallThickness + 0.01]}>
                        <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                    </mesh>
                }

                {params.add_storage_check && storageFrontWallModelObj?.innerWall && storageRightWallModelObj?.innerWall &&
                    <>
                        <mesh name="storage" geometry={storageFrontWallModelObj?.innerWall} position={[0, 0, -wallThickness + offset - 0.01]}> 
                            <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                        </mesh>
                        <mesh name="storage-right" geometry={storageRightWallModelObj?.innerWall} position={[-wallThickness - 0.01, 0, 0]}>
                            <meshBasicMaterial map={texture}  side={THREE.DoubleSide} color={"#C9C9C9"} transparent={params.is_translusant || (controls.isCheckActiveTab == "doorwindow" && const_var.focusedSection !== 'leftLean')} opacity={0.02} />
                        </mesh>
                    </>
                }
            </group>
        </>
    )
}

export default WallModel